export interface ProductionLineOrderLike {
  job_id?: string;
  website_job_number?: string | null;
  quantity?: number | string | null;
  glass_width?: number | string | null;
  glass_height?: number | string | null;
}

export interface JobGroupTotals {
  jobId: string;
  websiteJobNumber: string | null;
  totalOrders: number;
  totalQuantity: number;
  totalSquareMeters: number;
}

export interface OverallTotals {
  totalJobs: number;
  totalOrders: number;
  totalQuantity: number;
  totalSquareMeters: number;
}

export function calculateOrderSquareMeters(order: ProductionLineOrderLike): number {
  const quantity = parseInt(String(order.quantity ?? 0), 10) || 0;
  const width = parseFloat(String(order.glass_width ?? 0)) || 0;
  const height = parseFloat(String(order.glass_height ?? 0)) || 0;
  return (width * height * quantity) / 1_000_000;
}

export function formatSquareMeters(value: number): string {
  return value.toFixed(2);
}

export function buildJobTotalsMap(
  orders: ProductionLineOrderLike[]
): Map<string, JobGroupTotals> {
  const map = new Map<string, JobGroupTotals>();

  for (const order of orders) {
    const jobId = order.job_id ?? '';
    if (!jobId) continue;

    const quantity = parseInt(String(order.quantity ?? 0), 10) || 0;
    const sqm = calculateOrderSquareMeters(order);
    const existing = map.get(jobId);

    if (existing) {
      existing.totalOrders += 1;
      existing.totalQuantity += quantity;
      existing.totalSquareMeters += sqm;
    } else {
      map.set(jobId, {
        jobId,
        websiteJobNumber: order.website_job_number ?? null,
        totalOrders: 1,
        totalQuantity: quantity,
        totalSquareMeters: sqm,
      });
    }
  }

  return map;
}

export function calculateOverallTotals(orders: ProductionLineOrderLike[]): OverallTotals {
  const jobIds = new Set<string>();
  let totalQuantity = 0;
  let totalSquareMeters = 0;

  for (const order of orders) {
    const jobId = order.job_id ?? '';
    if (jobId) jobIds.add(jobId);
    totalQuantity += parseInt(String(order.quantity ?? 0), 10) || 0;
    totalSquareMeters += calculateOrderSquareMeters(order);
  }

  return {
    totalJobs: jobIds.size,
    totalOrders: orders.length,
    totalQuantity,
    totalSquareMeters,
  };
}

export function formatJobGroupHeader(totals: JobGroupTotals): string {
  const jobLabel = totals.websiteJobNumber ?? totals.jobId;
  return `Job ID: ${jobLabel} | Total Orders: ${totals.totalOrders} | Total Quantity: ${totals.totalQuantity} | Total Square Meters: ${formatSquareMeters(totals.totalSquareMeters)} m²`;
}

export function formatOverallTotalsFooter(totals: OverallTotals): string {
  return `Totals: Jobs: ${totals.totalJobs} | Orders: ${totals.totalOrders} | Quantity: ${totals.totalQuantity} | Square meters: ${formatSquareMeters(totals.totalSquareMeters)} m²`;
}
